import useLocalStorage from 'hooks/useLocalStorage';
import DataGridProp from 'models/dataGridProp';
import { createContext, Dispatch, useContext, useEffect, useState } from 'react';
import { ColumnsConfig, configureColumns } from 'utils/formatters';
import { ColumnType } from './WorkplaceStateProvider';

type GridColumnsConfigContextType<TableType extends DataGridProp, TSummaryRow = any> = {
    columnsConfig?: ColumnsConfig;
    setColumnsConfig: Dispatch<React.SetStateAction<ColumnsConfig | undefined>>;
    columns: ColumnType<TableType, TSummaryRow>[];
    initColumns: ColumnType<TableType, TSummaryRow>[];
};

const GridColumnsConfigContext = createContext<GridColumnsConfigContextType<any>>(
    {} as GridColumnsConfigContextType<any>
);

export const GridColumnsConfigProvider = <TableType extends DataGridProp, TSummaryRow = unknown>({
    children,
    storagePath,
    initColumns,
}: React.PropsWithChildren<{
    storagePath: string;
    initColumns: ColumnType<TableType, TSummaryRow>[];
}>) => {
    const [columnsConfig, setColumnsConfig] = useLocalStorage<ColumnsConfig>(storagePath);

    const fitColumns = () => {
        return configureColumns<TableType, TSummaryRow>(
            initColumns.map((x) => ({ ...x, visible: x.visible ?? true })),
            columnsConfig
        );
    };
    const [columns, setColumns] = useState<ColumnType<TableType, TSummaryRow>[]>(fitColumns());
    useEffect(() => setColumns(fitColumns()), [initColumns]);

    useEffect(() => {
        const newColumns = configureColumns(columns, columnsConfig);
        if (newColumns.length !== columns.length) {
            setColumns(newColumns);
        }
    }, [columnsConfig]);

    //TODO тормозит при изменении ширины колонок, надо бы оптимизировать
    useEffect(() => {
        setColumns((prev) => configureColumns(prev, columnsConfig));
    }, [columnsConfig]);

    return (
        <GridColumnsConfigContext.Provider
            value={{ columnsConfig, setColumnsConfig, columns, initColumns }}
        >
            {children}
        </GridColumnsConfigContext.Provider>
    );
};

export default GridColumnsConfigProvider;

const useGridColumnsConfig = <TableType extends DataGridProp, TSummaryRow = any>() => {
    const context =
        useContext<GridColumnsConfigContextType<TableType, TSummaryRow>>(GridColumnsConfigContext);

    return context;
};

export { useGridColumnsConfig };
